import React, { useState, useEffect } from 'react';
import { FileText, Calculator, ShieldCheck, BarChart, Link, Calendar, DollarSign, Bell, BookOpen, Users, Lock, Presentation, Target } from 'lucide-react';
import SectionTitle from './text_components/SectionTitle';
import { motion, AnimatePresence } from 'framer-motion';


const resources = [
  {
    title: 'Sample Educational Event Presentations',
    icon: <Users className="w-6 h-6 text-blue-500" />,
    category: 'Education Events',
    content: [
      { name: 'Powerpoint Template', link: "https://docs.google.com/presentation/d/1MUArsbuPBtG6BPl0FfhJulwmrP-J4Xga/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true" }
    ]
  },
  {
    title: 'Common Concerns for Age Groups',
    icon: <Presentation className="w-6 h-6 text-blue-500" />,
    category: 'Education Events',
    content: [
      { name: '20-30 Year Olds', link: 'https://drive.google.com/file/d/1HgAdwRUs_VFxCe3gu0_kGCZbMW203utt/view?usp=drive_link' },
      { name: '30-40 Year Olds', link: 'https://drive.google.com/file/d/1nwppn1rTpN_B1n5iQr0oWLDji_zuMUqY/view?usp=drive_link' },
      { name: '40-50 Year Olds', link: 'https://drive.google.com/file/d/1eeK3imPoMZXX_I4Nvimzfnu3bdamkeBx/view?usp=drive_link' },
      { name: '50-60 Year Olds', link: 'https://drive.google.com/file/d/1I0032dff_utGVpdNf7fwk80yBe2HwYn-/view?usp=drive_link' },
      { name: '60+ Year Olds', link: 'https://drive.google.com/file/d/1ysZl3fgvLfRmCeOHL9fR_ajg03Rg_uo5/view?usp=drive_link' },
    ]
  },
  {
    title: 'Traditional vs. Roth IRA',
    icon: <Target className="w-6 h-6 text-blue-500" />,
    category: 'Education Events',
    content: [
      { name: 'Roth IRA vs. Traditional IRA - Charles Schwab', link: 'https://www.schwab.com/ira/roth-vs-traditional-ira' },
      { name: 'Roth vs. Traditional IRA Calculator - Charles Schwab', link: 'https://www.schwab.com/ira/ira-calculators/roth-vs-traditional' },
    ]
  },
  {
    title: 'Financial Literacy Resources',
    icon: <DollarSign className="w-6 h-6 text-blue-500" />,
    category: 'Education Events',
    content: [
      { name: 'What is Financial Literacy? - Financial Literacy 101', link: 'https://financialliteracy101.org/financial-literacy/index.cfm?' },
      { name: 'Investing Basics: FAQs - Charles Schwab', link: 'https://www.schwab.com/how-to-invest/investing-basics' },
      { name: '6 Strategies to Lower Your Tax Bill - Investopedia', link: 'https://www.investopedia.com/articles/personal-finance/032116/top-6-strategies-protect-your-income-taxes.asp' },
      { name: "How To Start Investing in Stocks in 2025 and Beyond", link: 'https://www.investopedia.com/articles/basics/06/invest1000.asp' },
    ]
  },


  { 
    title: 'Form 5500 Filing',
    description: 'Instructions and guides for Form 5500',
    icon: FileText,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS Form 5500 Overview', url: 'https://www.irs.gov/retirement-plans/form-5500-corner' },
      { name: 'DOL 5500 Guide', url: 'https://www.dol.gov/agencies/ebsa/employers-and-advisers/plan-administration-and-compliance/reporting-and-filing/form-5500' }
    ]
  },
  { 
    title: 'Fiduciary Duties',
    description: 'Checklist for fiduciary responsibilities',
    icon: ShieldCheck,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'DOL Fiduciary Responsibilities', url: 'https://www.dol.gov/general/topic/retirement/fiduciaryresp' },
      { name: 'ERISA Fiduciary Advisor', url: 'https://webapps.dol.gov/elaws/ebsa/fiduciary/' }
    ]
  },
  { 
    title: 'Participant Communications',
    description: 'Sample templates for participant notices',
    icon: Users,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'IRS Notices', url: 'https://www.irs.gov/retirement-plans/plan-participant-employee/sample-notice-to-interested-parties' },
      { name: 'DOL Model Notices', url: 'https://www.dol.gov/agencies/ebsa/laws-and-regulations/laws/affordable-care-act/for-employers-and-advisers/coverage-options-notice' }
    ]
  },
  { 
    title: 'Tax Information',
    description: 'Resources for retirement plan tax info',
    icon: BarChart,
    color: 'text-blue-500',
    category: 'Financial',
    links: [
      { name: 'IRS Retirement Plan Info', url: 'https://www.irs.gov/retirement-plans' },
      { name: 'DOL Tax Information', url: 'https://www.dol.gov/general/topic/retirement/taxes' }
    ]
  },
  { 
    title: 'Retirement Calculators',
    description: 'Tools for retirement planning',
    icon: Calculator,
    color: 'text-blue-500',
    category: 'Tools',
    links: [
      { name: 'Fidelity Calculator', url: 'https://www.fidelity.com/calculators-tools/retirement-calculator' },
      { name: 'Vanguard Calculator', url: 'https://retirementplans.vanguard.com/VGApp/pe/pubeducation/calculators/RetirementIncomeCalc.jsf' }
    ]
  },
  { 
    title: 'Key Agencies',
    description: 'Links to important government agencies',
    icon: Link,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS', url: 'https://www.irs.gov/' },
      { name: 'Department of Labor', url: 'https://www.dol.gov/' },
      { name: 'PBGC', url: 'https://www.pbgc.gov/' }
    ]
  },
  { 
    title: 'Compliance Calendar',
    description: 'Key dates and deadlines for 401k plans',
    icon: Calendar,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'ERISA Compliance Calendar', url: 'https://si-interactive.s3.amazonaws.com/prod/plansponsor-com/wp-content/uploads/2024/01/09140643/PS-120923_2024-ERISA-Compliance-Calendar_p1.pdf' },
      { name: 'IRS Retirement Plan Deadlines', url: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-required-minimum-distributions-rmds' }
    ]
  },
  { 
    title: 'Fee Disclosure Guide',
    description: 'Information on 401k fee disclosures',
    icon: DollarSign,
    color: 'text-blue-500',
    category: 'Financial',
    links: [
      { name: 'DOL Fee Disclosure Guidance', url: 'https://www.dol.gov/agencies/ebsa/employers-and-advisers/guidance/field-assistance-bulletins/2012-02' },
      { name: 'ERISA 408(b)(2) Resource Guide', url: 'https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/fact-sheets/fact-sheet-service-provider-disclosure-regulation.pdf' }
    ]
  },
  { 
    title: 'Plan Design Resources',
    description: 'Guides for 401k plan structure and features',
    icon: BookOpen,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS Plan Feature Comparison', url: 'https://www.irs.gov/retirement-plans/plan-sponsor/types-of-retirement-plans' },
      { name: 'DOL 401k Plans Guide', url: 'https://www.dol.gov/agencies/ebsa/employers-and-advisers/plan-administration-and-compliance/retirement' }
    ]
  },
  { 
    title: 'Investment Education',
    description: 'Resources for educating participants',
    icon: BarChart,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'FINRA Investor Education', url: 'https://www.finra.org/investors/learn-to-invest' },
      { name: 'SEC Investor.gov', url: 'https://www.investor.gov/' }
    ]
  },
  { 
    title: 'Cybersecurity Best Practices',
    description: 'Guidance on protecting retirement plans',
    icon: Lock,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'DOL Cybersecurity Guidance', url: 'https://www.dol.gov/agencies/ebsa/key-topics/retirement-benefits/cybersecurity' },
      { name: 'SPARK Institute Cybersecurity', url: 'https://www.sparkinstitute.org/content-areas/cybersecurity/' }
    ]
  },
  { 
    title: 'Industry Updates',
    description: 'Stay current with 401k news and changes',
    icon: Bell,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'NAPA Net', url: 'https://www.napa-net.org/' },
      { name: "NAPA Events", url: "https://www.napa-net.org/events/" },
      { name: '401k Specialist Magazine', url: 'https://401kspecialistmag.com/' }
    ]
  },
  { 
    title: 'Plan Document Templates',
    description: 'Sample templates for File Library and amendments.',
    icon: FileText,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS Checklists for Retirement Plan Documents', url: 'https://www.irs.gov/retirement-plans/irs-checklists-for-retirement-plan-documents' },
      { name: 'ERISA Documentation Disclosures and Reporting', url: 'https://www.lyonsinsurance.com/blog/erisa-documentation-disclosures-reporting/' }
    ]
  },
  { 
    title: 'Legislative Updates',
    description: 'Stay informed about recent legislative changes affecting retirement plans.',
    icon: Bell,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'Congressional Research Service Reports', url: 'https://crsreports.congress.gov/' },
      { name: 'SECURE Act Summary - ADP', url: 'https://www.adp.com/what-we-offer/benefits/retirement/secure-2.aspx#:~:text=In%20summary%2C%20the%20Secure%20Act,enrollment%2C%20expanding%20and%20matching%20contributions.' }
    ]
  },
  { 
    title: 'Employee Engagement Strategies',
    description: 'Techniques to increase employee participation in retirement plans.',
    icon: Users,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'SHRM Employee Engagement', url: 'https://www.shrm.org/resourcesandtools/hr-topics/employee-relations/pages/default.aspx' },
      { name: '5 Tips to Increase Plan Participation', url: 'https://www.envoyfinancial.com/5-tips-to-increase-plan-participation/' }
    ]
  },
  { 
    title: 'Plan Benchmarking Tools',
    description: 'Compare your plan\'s performance to industry standards.',
    icon: BarChart,
    color: 'text-blue-500',
    category: 'Tools',
    links: [
      { name: 'BrightScope Ratings', url: 'https://www.brightscope.com/ratings/' },
      { name: 'DOL Form 5500 Search', url: 'https://www.efast.dol.gov/portal/app/disseminate?execution=e2s1' }
    ]
  },
  { 
    title: 'Recordkeeping Best Practices',
    description: 'Guidelines for effective plan recordkeeping.',
    icon: FileText,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS Recordkeeping Requirements', url: 'https://www.irs.gov/businesses/small-businesses-self-employed/recordkeeping' },
      { name: 'DOL Record Retention Requirements', url: 'https://www.dol.gov/agencies/whd/fact-sheets/21-flsa-recordkeeping' },
      { name: 'DOL Recordkeeping and Reporting', url: 'https://www.dol.gov/general/topic/wages/wagesrecordkeeping'}
    ]
  },
  { 
    title: 'Participant Loan Guidelines',
    description: 'Information on loans from retirement plans.',
    icon: DollarSign,
    color: 'text-blue-500',
    category: 'Financial',
    links: [
      { name: 'IRS Retirement Plan Loans FAQs', url: 'https://www.irs.gov/retirement-plans/retirement-plans-faqs-regarding-loans' },
      { name: 'DOL Plan Loans', url: 'https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-loans' }
    ]
  },
  { 
    title: 'Retirement Income Strategies',
    description: 'Resources for planning retirement income.',
    icon: DollarSign,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'Consumer Financial Protection Bureau', url: 'https://www.consumerfinance.gov/consumer-tools/retirement/' },
      { name: 'Social Security Administration', url: 'https://www.ssa.gov/benefits/retirement/' }
    ]
  },
  { 
    title: 'Investment Policy Statement Guidance',
    description: 'How to create and maintain an Investment Policy Statement.',
    icon: FileText,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'DOL Tips for ERISA Fiduciaries', url: 'https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/fact-sheets/target-date-retirement-funds-erisa-plan-fiduciaries-tips.pdf' },
      // { name: 'Sample Investment Policy Statement', url: 'https://www.napa-net.org/news-info/daily-news/sample-investment-policy-statement-now-available' }
    ]
  },
  { 
    title: 'Plan Correction Programs',
    description: 'Resources for correcting plan errors.',
    icon: ShieldCheck,
    color: 'text-blue-500',
    category: 'Compliance',
    links: [
      { name: 'IRS EPCRS Overview', url: 'https://www.irs.gov/retirement-plans/epcrs-overview' },
      { name: 'DOL Voluntary Fiduciary Correction Program', url: 'https://www.dol.gov/agencies/ebsa/employers-and-advisers/plan-administration-and-compliance/correction-programs/vfcp' }
    ]
  },
  { 
    title: 'Fiduciary Training',
    description: 'Educational materials for plan fiduciaries.',
    icon: BookOpen,
    color: 'text-blue-500',
    category: 'Education Events',
    links: [
      { name: 'DOL Fiduciary Education Campaign', url: 'https://www.dol.gov/agencies/ebsa/employers-and-advisers/plan-administration-and-compliance/fiduciary-education' },
      { name: 'IFEBP Fiduciary Responsibility', url: 'https://www.ifebp.org/resources---news/toolkits/employee-benefits-in-canada/fiduciary-responsibility' }
    ]
  },
  {
    title: 'Proposals',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Proposals',
    content: [
      { name: 'Record Keeper Comparison Proposal Template', link: 'https://docs.google.com/spreadsheets/d/1bgsT4wZ2upxk9HBl5DRGimFSTrcT52U8/edit?gid=1460206250#gid=1460206250' }, 
      { name: 'Plan Proposal Presentation Template - Teal/Black', link: 'https://docs.google.com/presentation/d/1c2-aKzguJIZsuY_ZHcGk-Sk_e8KIOgob/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true'},
      { name: 'Plan Proposal Presentation Template - Teal/White', link: 'https://docs.google.com/presentation/d/13luIOnD7Kre-hflaAyMN0Q44GO9UHPJb/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true'},
      { name: 'Plan Proposal Presentation Template - Green/Black', link: 'https://docs.google.com/presentation/d/1c2-aKzguJIZsuY_ZHcGk-Sk_e8KIOgob/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true' },
      { name: 'Plan Proposal Presentation Template - Green/White', link: 'https://docs.google.com/presentation/d/15H4muKlhBLvViGTOzVMJMbeNuslQ9XpF/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true' },
      { name: 'Plan Proposal Presentation Template - Red/Black', link: 'https://docs.google.com/presentation/d/1Q4PhB0GVfQ3bNssJ4e0D_aZCC8ySMUbs/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true'},
      { name: 'Plan Proposal Presentation Template - Red/White', link: 'https://docs.google.com/presentation/d/1ts3hmPq5DZ-le0JvNvH4j_o6VqFwWR1C/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true'},
      { name: 'Plan Proposal Presentation Template - Orange/White', link: 'https://docs.google.com/presentation/d/1UiM-mFGIP1HCoyIEGZbYIRgLALNK5x1x/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true'},
      { name: 'Plan Proposal Presentation Template - Orange/Black', link: 'https://docs.google.com/presentation/d/1w_WJS12QszJzOCR54MtzXCCF39eNYxqj/edit?usp=drive_link&ouid=111485210408989043988&rtpof=true&sd=true'},
    ]
  },
  {
    title: 'Fidelity Investments Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://401k.fidelity.com/' }
    ]
  },
  {
    title: 'Empower Retirement Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.empower.com/' }
    ]
  },
  {
    title: 'Vanguard Group Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://advisors.vanguard.com/' }
    ]
  },
  {
    title: 'Principal Financial Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://login.principal.com/' }
    ]
  },
  {
    title: 'T. Rowe Price Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.troweprice.com/financial-intermediary' }
    ]
  },
  {
    title: 'Charles Schwab Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://workplace.schwab.com/' }
    ]
  },
  {
    title: 'Ascensus Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.ascensus.com/' }
    ]
  },
  {
    title: 'Transamerica Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.transamerica.com/financial-professional' }
    ]
  },
  {
    title: 'ADP Retirement Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.adp.com/what-we-offer/benefits/retirement.aspx' }
    ]
  },
  {
    title: 'Paychex Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.paychex.com/login' }
    ]
  },
  {
    title: 'John Hancock Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://myplan.johnhancock.com' }
    ]
  },
  {
    title: 'American Funds Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.americanfunds.com/rps/login' }
    ]
  },
  {
    title: 'Newport Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.newportgroup.com/' }
    ]
  },
  {
    title: 'Alerus Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.alerus.com/' }
    ]
  },
  {
    title: 'Voya Financial Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://advisors.voya.com/' }
    ]
  },
  {
    title: 'Alight Solutions Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://alight.com/' }
    ]
  },
  {
    title: 'Nationwide Portal',
    icon: <Link className="w-6 h-6 text-blue-500" />,
    category: 'Recordkeepers',
    content: [
      { name: 'Advisor Portal Login', link: 'https://www.nationwidefinancial.com/advisor-grant-access' }
    ]
  }
];

const categories = [
  { name: 'All', icon: BookOpen },
  { name: 'Education Events', icon: Presentation },
  { name: 'Compliance', icon: ShieldCheck },
  { name: 'Financial', icon: DollarSign },
  { name: 'Tools', icon: Calculator },
  { name: 'Recordkeepers', icon: Link },
  { name: 'Proposals', icon: Presentation}
];

const AdvisorResources = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Reduce the loading time to make the transition smoother
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const filteredResources = resources
    .filter(resource => {
      const matchesCategory = selectedCategory === 'All' || resource.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (resource.content || []).some(item => 
          item.name.toLowerCase().includes(searchQuery.toLowerCase())
        ) ||
        (resource.links || []).some(item =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      return matchesCategory && matchesSearch;
    });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      }
    }
  };

  const ResourceCard = ({ title, icon: Icon, links, content, color }) => (
    <motion.div
      variants={itemVariants}
      className="bg-gradient-to-br from-white to-gray-100 rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-blue-200 flex flex-col"
    >
      <div className={`p-6 ${color} bg-opacity-10 h-24 flex items-center`} style={{ paddingBottom: '0.25rem', paddingTop: '0.5rem' }}>
        <div className="flex items-center space-x-4">
          {React.isValidElement(Icon) ? Icon : <Icon className={`h-8 w-8 ${color}`} />}
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
        </div>
      </div>
      <div className="p-6 space-y-3 flex-grow" style={{ paddingTop: '0.25rem'}}>
        {(links || content || []).map((item, index) => (
          <a
            key={index}
            href={item.url || item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-3 px-4 bg-blue-50 border border-gray-200 shadow-2xl rounded-xl text-sm font-medium text-blue-700 hover:bg-blue-100 transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105"
          >
            {item.name}
          </a>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-12">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="mx-auto"
      >
        <motion.div variants={itemVariants}>
          {/* <SectionTitle className="text-4xl font-bold text-gray-800 text-center mb-8">Advisor Resources</SectionTitle> */}
        </motion.div>

        <motion.nav 
          variants={itemVariants} 
          className="flex flex-col gap-6 mb-4 p-8 bg-white shadow-xl rounded-2xl border border-blue-200"
        >
          {/* Search bar */}
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 bg-gray-50 border border-blue-200 rounded-xl shadow-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-300 text-gray-700 placeholder-gray-400"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <svg 
                className="w-6 h-6" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                />
              </svg>
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`flex items-center px-6 py-3 rounded-full transition-all duration-300 ${
                  selectedCategory === category.name
                    ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <category.icon className={`w-5 h-5 mr-2 ${selectedCategory === category.name ? 'text-white' : 'text-blue-500'}`} />
                <span className="text-sm font-medium">{category.name}</span>
              </button>
            ))}
          </div>
        </motion.nav>

        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center h-64"
            >
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
            >
              {filteredResources.map((resource, index) => (
                <ResourceCard key={index} {...resource} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AdvisorResources;